import { useEffect, useState } from "react";
import axios from "axios";
import SimplePage from "@/components/layout/SimplePage";
import RichHtml from "@/components/RichHtml";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Section render order + human-readable heading for each
const SECTIONS = [
  { key: "office", label: "Office Administrator" },
  { key: "clergy", label: "Spiritual Leader" },
  { key: "religious_school", label: "Religious School Director" },
  { key: "youth", label: "Youth Group Leaders" },
  { key: "board", label: "Board of Directors" },
  { key: "other", label: "Team" },
];

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/staff`)
      .then(r => setStaff(r.data || []))
      .catch(() => setStaff([]))
      .finally(() => setLoaded(true));
  }, []);

  const grouped = SECTIONS
    .map(sec => ({
      ...sec,
      items: staff.filter(s => s.section === sec.key)
                  .sort((a, b) => a.display_order - b.display_order),
    }))
    .filter(sec => sec.items.length > 0);

  return (
    <SimplePage
      title="Meet Our Staff"
      eyebrow="Our Team"
      subtitle="The clergy, educators, and volunteers who make TIOB feel like home, day in and day out."
      path="/about/staff"
      ctaLabel="Get in touch"
    >
      {!loaded ? (
        <p className="text-[color:var(--muted)]">Loading team…</p>
      ) : grouped.length === 0 ? (
        <p>No staff to display yet.</p>
      ) : (
        grouped.map(sec => (
          <div key={sec.key}>
            <h2>{sec.label}</h2>
            {sec.items.map(person => <PersonCard key={person.id} person={person} />)}
          </div>
        ))
      )}

      <h2>Volunteer Coordinators</h2>
      <p>
        Many of our programs &mdash; from Sisterhood to Men&rsquo;s Club to Social Action &mdash; are
        staffed by dedicated congregant-volunteers. If you&rsquo;d like to get involved or find out who
        coordinates a specific program, please contact the office and we will connect you with the right
        person.
      </p>
    </SimplePage>
  );
}

function PersonCard({ person }) {
  const photo = person.photo_url
    ? (person.photo_url.startsWith("/api/")
        ? `${process.env.REACT_APP_BACKEND_URL}${person.photo_url}`
        : person.photo_url)
    : null;

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "1.75rem",
        alignItems: "flex-start",
        marginBottom: "1.5rem",
      }}
      data-testid={`staff-card-${person.id}`}
    >
      {photo && (
        <img
          src={photo}
          alt={person.name}
          style={{
            width: "180px",
            borderRadius: "16px",
            boxShadow: "0 6px 20px -6px rgba(15,95,170,0.25)",
            flexShrink: 0,
          }}
          loading="lazy"
        />
      )}
      <div style={{ flex: "1 1 260px", minWidth: 0 }}>
        <h3 style={{ marginTop: 0 }}>{person.name}</h3>
        {person.role && (
          <p style={{ marginTop: 0, color: "var(--muted)", fontStyle: "italic" }}>
            {person.role}
          </p>
        )}
        {person.bio && <RichHtml html={person.bio} className="" />}
        {(person.phone || person.email || person.hours) && (
          <p>
            {person.phone && (
              <>
                <strong>Phone:</strong>{" "}
                <a href={`tel:${person.phone.replace(/[^0-9+]/g, "")}`}>{person.phone}</a><br />
              </>
            )}
            {person.email && (
              <>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${person.email}`}>{person.email}</a><br />
              </>
            )}
            {person.hours && (
              <>
                <strong>Hours:</strong> {person.hours}
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
}
